import { generateClient, type GraphQLResult } from 'aws-amplify/api'

/**
 * Client GraphQL lazy: `generateClient()` esige che `Amplify.configure()` sia
 * già stato chiamato. Se creassimo il client al top-level (import time),
 * verrebbe instanziato prima del bootstrap in main.tsx e Amplify lancerebbe
 * il warning "Amplify has not been configured".
 *
 * Lo creiamo alla prima chiamata di `gql()`, e lo cache-iamo per tutto il ciclo.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _client: any = null
function getClient() {
  if (!_client) _client = generateClient()
  return _client
}

/**
 * Errore GraphQL tipizzato dell'applicazione.
 * Distinguiamo 3 famiglie:
 *  - `auth`: token scaduto, identity rifiutata → utente verrà ridirezionato a /login
 *  - `network`: backend non raggiungibile → retry plausibile
 *  - `graphql`: il resolver ha risposto con errors[] → bug o dato mancante
 */
export type GraphQLErrorKind = 'auth' | 'network' | 'graphql'

export class AppGraphQLError extends Error {
  kind: GraphQLErrorKind
  /** Raw errors[] dal payload AppSync, se applicabile. */
  graphQLErrors?: unknown[]
  /** Eventuale eccezione lower-level (fetch, parse). */
  cause?: unknown

  constructor(kind: GraphQLErrorKind, message: string, opts?: { graphQLErrors?: unknown[]; cause?: unknown }) {
    super(message)
    this.name = 'AppGraphQLError'
    this.kind = kind
    this.graphQLErrors = opts?.graphQLErrors
    this.cause = opts?.cause
  }
}

/**
 * Riconosce gli errori auth-related di Amplify.
 * I messaggi possono variare lievemente tra versioni del SDK, quindi si
 * confrontano con la lista più comune.
 */
function isAuthError(err: unknown): boolean {
  if (!err) return false
  const msg = err instanceof Error ? err.message : String(err)
  return (
    /No current user|NotAuthorizedException|Token has expired|Refresh Token has expired|UnauthorizedException|unauthorized/i.test(
      msg,
    )
  )
}

function isNetworkError(err: unknown): boolean {
  if (!err) return false
  const msg = err instanceof Error ? err.message : String(err)
  return /NetworkError|Failed to fetch|fetch failed|TypeError: Failed/i.test(msg)
}

/**
 * Esegue una query/mutation GraphQL contro AppSync.
 * - Auth header (idToken Cognito) gestito automaticamente da Amplify
 * - Converte qualsiasi failure in `AppGraphQLError` tipizzato
 * - Ritorna direttamente il payload tipato `T`
 */
export async function gql<T>(
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> {
  try {
    // Amplify v6 ha tipizzazione stretta delle variables tramite generics dal
    // documento. Usiamo stringhe GraphQL grezze, quindi facciamo un cast a
    // `any` localizzato per accettare un dizionario di variables generico.
    const result = (await getClient().graphql({
      query,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      variables: variables as any,
    })) as GraphQLResult<T>

    if (result.errors && result.errors.length > 0) {
      throw new AppGraphQLError('graphql', result.errors[0]?.message ?? 'GraphQL error', {
        graphQLErrors: result.errors,
      })
    }

    if (!result.data) {
      throw new AppGraphQLError('graphql', 'Empty data')
    }

    return result.data
  } catch (err) {
    if (err instanceof AppGraphQLError) throw err

    if (isAuthError(err)) {
      throw new AppGraphQLError('auth', 'Sessione scaduta. Effettua di nuovo l’accesso.', {
        cause: err,
      })
    }
    if (isNetworkError(err)) {
      throw new AppGraphQLError('network', 'Connessione assente o backend irraggiungibile.', {
        cause: err,
      })
    }

    // Amplify wrappa GraphQL errors in { errors: [...] } senza eccezione → caso raro
    // di errore generico.
    const message = err instanceof Error ? err.message : 'Errore sconosciuto'
    throw new AppGraphQLError('graphql', message, { cause: err })
  }
}
